import { captureException } from "@sentry/browser";
import { t } from "ttag";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import {
    IReviewsBrandID,
    IReviewsProductID,
    ProductUUID,
} from "../models/nominals";
import {
    IReviewResultsPage,
    IReviewsBrand,
    IReviewsProduct,
    ISearchFacet,
    ISearchReviewArgs,
    IWriteReviewData,
    IWriteReviewTemplate,
    ReviewResultsPage,
    ReviewSortOption,
    ReviewsBrand,
    ReviewsProduct,
    ReviewsProducts,
    SearchFacets,
    WriteReviewTemplate,
} from "../models/reviews";
import { check } from "../models/utils";
import { fetchJson } from "../utils/ajax";
import { objectKeys } from "../utils/functional";
import { intersection } from "../utils/sets";
import { getPageSetting } from "../utils/settings";

// Declare global exposed by IOVATION Blackbox script
declare function ioGetBlackbox(): { blackbox: string };

// Reviews API version is hardcoded as it's unlikely to change
const REVIEWS_API_VERSION = 2;

export const getReviewAPIURL = (path: string): string => {
    let apiBase = getPageSetting("reviews_api");
    if (!apiBase) {
        throw new Error("Reviews API base URL is not configured");
    }
    if (apiBase.substr(-1) === "/") {
        apiBase = apiBase.substr(0, apiBase.length - 1);
    }
    if (path.substr(0, 1) === "/") {
        path = path.substr(1);
    }
    return `${apiBase}/api/v${REVIEWS_API_VERSION}/${path}`;
};

export const getReviewsBrand = async (
    brandID: IReviewsBrandID,
): Promise<IReviewsBrand> => {
    const apiURL = getReviewAPIURL(`/brands/${brandID}`);
    const data = await fetchJson(apiURL, {
        credentials: "omit", // External API
    });
    return check(ReviewsBrand.decode(data));
};

export const getProducts = async (
    brandID: IReviewsBrandID,
): Promise<readonly IReviewsProduct[]> => {
    const apiURL = getReviewAPIURL(`/products`);
    const params = new URLSearchParams({ brand: String(brandID) });
    const data = await fetchJson(`${apiURL}?${params}`, {
        credentials: "omit", // External API
    });
    return check(ReviewsProducts.decode(data));
};

export const getProductsByUUIDs = async (
    productUUIDs: readonly ProductUUID[],
): Promise<readonly IReviewsProduct[]> => {
    const apiURL = getReviewAPIURL(`/products`);
    const data = await fetchJson(apiURL, {
        credentials: "omit", // External API
    });
    const products = check(ReviewsProducts.decode(data));
    const queriedForUUIDs = new Set(productUUIDs);
    return products.filter((p) => {
        // Build set of UUIDs relevant to this reviews product
        const thisProductUUIDs = new Set([...(p.component_of_uuids || [])]);
        if (p.uuid) {
            thisProductUUIDs.add(p.uuid);
        }
        // Determine if this product matches any of the UUIDs being queried for. If
        // so, return it.
        const overlap = intersection(thisProductUUIDs, queriedForUUIDs);
        return overlap.size > 0;
    });
};

export const getProduct = async (
    productID: IReviewsProductID,
): Promise<IReviewsProduct> => {
    const apiURL = getReviewAPIURL(`/products/${productID}`);
    const data = await fetchJson(apiURL, {
        credentials: "omit", // External API
    });
    return check(ReviewsProduct.decode(data));
};

export const getSearchFacets = async (
    brandID: IReviewsBrandID,
    query: ISearchReviewArgs,
): Promise<readonly ISearchFacet[] | null> => {
    const apiURL = getReviewAPIURL(`/brands/${brandID}/facets`);
    delete query.ordering;
    delete query.page;
    delete query.page_size;
    // Remove empty keys
    objectKeys(query).forEach((key) => {
        if (!query[key]) {
            delete query[key];
        }
    });
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        if (value !== undefined && value !== null) {
            params.append(key, String(value));
        }
    }
    const data = await fetchJson(`${apiURL}?${params}`, {
        credentials: "omit", // External API
    });
    return check(SearchFacets.decode(data));
};

export const searchReviews = async (
    query: ISearchReviewArgs,
): Promise<IReviewResultsPage | null> => {
    const apiURL = getReviewAPIURL(`/reviews`);
    if (!query.ordering) {
        query.ordering = ReviewSortOption.HIGHEST_RATED;
    }
    if (!query.page) {
        query.page = 1;
    }
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        if (value !== undefined && value !== null) {
            params.append(key, String(value));
        }
    }
    const data = await fetchJson(`${apiURL}?${params}`, {
        credentials: "omit", // External API
    });
    return check(ReviewResultsPage.decode(data));
};

export const getWriteReviewTemplate = async (
    productID: IReviewsProductID,
): Promise<IWriteReviewTemplate> => {
    const apiURL = getReviewAPIURL(`/reviews/template`);
    const params = new URLSearchParams({ id: String(productID) });
    const data = await fetchJson(`${apiURL}?${params}`, {
        credentials: "omit", // External API
    });
    const baseTemplate = check(WriteReviewTemplate.decode(data));
    // Slightly amend the template returned from the server to make the email_collection field mandatory
    // If the template returned from the server does not contain email_collection field, populate all the
    // template field data given that we make the email_collection field mandatory
    const template: IWriteReviewTemplate = {
        ...baseTemplate,
        email_collection: {
            ...(baseTemplate.email_collection || {
                type: "email",
                response_type: "email",
                input_type: "TextBox",
                field_id: null,
                field_type: "simple",
                key: "email_collection",
                name: t`Your email address`,
                max_length: null,
                options: [],
                is_tag: false,
                is_merchant_specific: false,
            }),
            is_required: true,
        },
    };
    return template;
};

const iglooGetBlackbox = (retry: number): string => {
    const window = getWindow();
    const tryGetBlackbox = (attempt: number): string => {
        if (!window || typeof window.IGLOO?.getBlackbox !== "function") {
            throw new Error(t`window.IGLOO.getBlackbox is not a function.`);
        }
        const blackboxData = window.IGLOO.getBlackbox();
        if (blackboxData.finished) {
            return blackboxData.blackbox;
        }
        if (attempt >= retry) {
            throw new Error(
                t`The device fingerprint blackbox string couldn't be retrieved after ${retry} attempts.`,
            );
        }
        return tryGetBlackbox(attempt + 1);
    };
    return tryGetBlackbox(1);
};

export const submitReview = async (
    productID: IReviewsProductID,
    started: string,
    fields: { [k: string]: string | string[] },
): Promise<void> => {
    const apiURL = getReviewAPIURL(`/reviews/new`);
    const blackbox = {
        io: "",
        igloo: "",
    };
    try {
        blackbox.io = ioGetBlackbox().blackbox;
        blackbox.igloo = iglooGetBlackbox(5);
    } catch (e) {
        captureException(e);
    }
    const data: IWriteReviewData = {
        ...fields,
        id: `${productID}`,
        blackbox: blackbox,
        started: started,
        created: new Date().toISOString(),
    };
    try {
        await fetchJson(apiURL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
            credentials: "omit", // External API
        });
    } catch (err) {
        captureException(err);
        throw err;
    }
};
